/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */
import React from "react";
import { Banner } from "@forge/common";
import { useTranslation } from "@scuf/localization";
import { localeKeyMap } from "../../../localization/keys";
import "./NotificationBanner.scss";

interface NotificationBannerProps {
  /** Whether to show the banner */
  isVisible: boolean;
  /** Optional custom title for the banner */
  title?: string;
  /** Optional custom description for the banner */
  description?: string;
  /** Banner type - defaults to "info" */
  type?: "info" | "success" | "warning" | "error";
  /** Banner variant - defaults to "inline" */
  variant?: "page" | "inline" | "secondary" | "high-contrast";
  /** Whether to show icon - defaults to false */
  hideIcon?: boolean;
  /** Whether the banner can be closed manually - defaults to false */
  canClose?: boolean;
  /** Callback when banner is closed manually */
  onClose?: () => void;
  /** Optional custom actions for the banner (React elements) */
  actions?: React.ReactNode[];
  /** Optional locale key for internationalized title */
  titleLocaleKey?: string;
  /** Optional locale key for internationalized description */
  descriptionLocaleKey?: string;
  /** Additional CSS class names */
  className?: string;
}

const NotificationBanner: React.FC<NotificationBannerProps> = ({
  isVisible,
  title,
  description,
  type = "info",
  variant = "inline",
  hideIcon = false,
  canClose = false,
  onClose,
  actions = [],
  titleLocaleKey,
  descriptionLocaleKey,
  className,
}) => {
  const [t] = useTranslation();

  if (!isVisible) {
    return null;
  }

  // Get localized text if keys are provided
  const localizedTitle = titleLocaleKey
    ? t(localeKeyMap[titleLocaleKey as keyof typeof localeKeyMap])
    : undefined;
  const localizedDescription = descriptionLocaleKey
    ? t(localeKeyMap[descriptionLocaleKey as keyof typeof localeKeyMap])
    : undefined;

  // Fallback hierarchy: provided text -> localized text -> default text
  const bannerTitle = title || localizedTitle || "";
  const bannerDescription = description || localizedDescription || "";

  return (
    <Banner
      actions={actions}
      description={bannerDescription}
      canClose={canClose}
      type={type}
      variant={variant}
      title={bannerTitle}
      hideIcon={hideIcon}
      onClose={onClose}
      className={"notification-banner"}
    />
  );
};

export default NotificationBanner;
